import { create } from "zustand"

export type SessionMergeRequestPhase =
  | "idle"
  | "requested"
  | "failed"
  | "succeeded"
  | "indeterminate"

export type SessionMergeFailureKind =
  | "conflicting"
  | "behind"
  | "blocked"
  | "draft"
  | "unknown"

export interface SessionMergeRequestState {
  phase: SessionMergeRequestPhase
  requestId: string | null
  error: string | null
  mergeConflict: boolean
  failureKind: SessionMergeFailureKind | null
  timestamp: number | null
}

export type SessionResolveConflictPhase =
  | "idle"
  | "requested"
  | "started"
  | "dispatched"
  | "failed"
  | "indeterminate"

export interface SessionResolveConflictActionState {
  phase: SessionResolveConflictPhase
  requestId: string | null
  conversationId: string | null
  baseBranch: string | null
  dispatchMode: "queued" | "forwarded" | null
  error: string | null
  timestamp: number | null
}

interface SessionPrActionStateStore {
  mergeRequests: Record<string, SessionMergeRequestState>
  resolveConflictActions: Record<string, SessionResolveConflictActionState>
  startMergeRequest: (args: { sessionId: string; requestId: string; timestamp?: number }) => void
  settleMergeRequest: (args: {
    sessionId: string
    requestId?: string | null
    ok: boolean
    error?: string | null
    mergeConflict?: boolean
    failureKind?: SessionMergeFailureKind | null
    timestamp?: number
  }) => void
  markMergeRequestIndeterminate: (args: {
    sessionId: string
    message: string
    timestamp?: number
  }) => void
  requestResolveConflict: (args: { sessionId: string; requestId: string; conversationId?: string | null; timestamp?: number }) => void
  setResolveConflictFromServer: (args: {
    sessionId: string
    requestId?: string | null
    conversationId?: string | null
    state: "started" | "dispatched" | "failed"
    baseBranch?: string | null
    dispatchMode?: "queued" | "forwarded" | null
    error?: string | null
    timestamp?: number
  }) => void
  clearResolveConflictFromStatus: (args: {
    sessionId: string
    conversationId?: string | null
    timestamp?: number
    observedAt?: number
  }) => void
  markResolveConflictIndeterminate: (args: {
    sessionId: string
    message: string
    timestamp?: number
  }) => void
  clearMergeRequestState: (sessionId: string) => void
  clearSessionActionState: (sessionId: string) => void
}

const RESOLVE_CONFLICT_STATUS_CONFIRMATION_GRACE_MS = 5_000
const RESOLVE_CONFLICT_STATUS_CONFIRMATION_MESSAGE =
  "Checking whether conflict resolution started on the server."

function buildIdleMergeRequestState(): SessionMergeRequestState {
  return {
    phase: "idle",
    requestId: null,
    error: null,
    mergeConflict: false,
    failureKind: null,
    timestamp: null,
  }
}

function buildIdleResolveConflictActionState(): SessionResolveConflictActionState {
  return {
    phase: "idle",
    requestId: null,
    conversationId: null,
    baseBranch: null,
    dispatchMode: null,
    error: null,
    timestamp: null,
  }
}

function resolveTimestamp(value?: number): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.floor(value) : Date.now()
}

function isNewerOrEqualTimestamp(args: {
  previousTimestamp: number | null | undefined
  nextTimestamp: number
}): boolean {
  if (typeof args.previousTimestamp !== "number" || !Number.isFinite(args.previousTimestamp)) {
    return true
  }
  return args.nextTimestamp >= Math.floor(args.previousTimestamp)
}

function shouldKeepResolveConflictRequestPending(args: {
  requestedAt: number | null
  observedMissingAt: number
}): boolean {
  if (typeof args.requestedAt !== "number" || !Number.isFinite(args.requestedAt)) {
    return false
  }
  return args.observedMissingAt < Math.floor(args.requestedAt) + RESOLVE_CONFLICT_STATUS_CONFIRMATION_GRACE_MS
}

function shouldKeepResolveConflictIndeterminatePending(args: {
  indeterminateAt: number | null
  observedMissingAt: number
}): boolean {
  if (typeof args.indeterminateAt !== "number" || !Number.isFinite(args.indeterminateAt)) {
    return false
  }
  return args.observedMissingAt < Math.floor(args.indeterminateAt) + RESOLVE_CONFLICT_STATUS_CONFIRMATION_GRACE_MS
}

function isLocalResolveConflictHintPhase(
  phase: SessionResolveConflictPhase,
): phase is Extract<SessionResolveConflictPhase, "requested" | "indeterminate"> {
  return phase === "requested" || phase === "indeterminate"
}

function hasResolveConflictRecoveryRequestContext(state: SessionResolveConflictActionState): boolean {
  return (
    state.phase === "requested" ||
    state.phase === "indeterminate" ||
    (state.phase === "idle" && typeof state.requestId === "string" && state.requestId.length > 0)
  )
}

function shouldAcceptMergeRequestSettlement(args: {
  previous: SessionMergeRequestState
  nextRequestId?: string | null
  nextTimestamp: number
}): boolean {
  const sameRequestId =
    typeof args.nextRequestId === "string" &&
    args.nextRequestId.length > 0 &&
    args.nextRequestId === args.previous.requestId

  if (sameRequestId && (args.previous.phase === "requested" || args.previous.phase === "indeterminate")) {
    return true
  }

  return isNewerOrEqualTimestamp({
    previousTimestamp: args.previous.timestamp,
    nextTimestamp: args.nextTimestamp,
  })
}

function shouldAcceptResolveConflictServerUpdate(args: {
  previous: SessionResolveConflictActionState
  nextRequestId?: string | null
  nextTimestamp: number
}): boolean {
  const sameRequestId =
    typeof args.nextRequestId === "string" &&
    args.nextRequestId.length > 0 &&
    args.nextRequestId === args.previous.requestId

  if (sameRequestId && hasResolveConflictRecoveryRequestContext(args.previous)) {
    return true
  }

  return isNewerOrEqualTimestamp({
    previousTimestamp: args.previous.timestamp,
    nextTimestamp: args.nextTimestamp,
  })
}

export const useSessionPrActionState = create<SessionPrActionStateStore>((set) => ({
  mergeRequests: {},
  resolveConflictActions: {},

  startMergeRequest: ({ sessionId, requestId, timestamp }) => {
    if (!sessionId || !requestId) return
    set((state) => ({
      mergeRequests: {
        ...state.mergeRequests,
        [sessionId]: {
          phase: "requested",
          requestId,
          error: null,
          mergeConflict: false,
          failureKind: null,
          timestamp: resolveTimestamp(timestamp),
        },
      },
    }))
  },

  settleMergeRequest: ({ sessionId, requestId, ok, error, mergeConflict, failureKind, timestamp }) => {
    if (!sessionId) return
    set((state) => {
      const previous = state.mergeRequests[sessionId] ?? buildIdleMergeRequestState()
      const nextTimestamp = resolveTimestamp(timestamp)
      if (!shouldAcceptMergeRequestSettlement({
        previous,
        nextRequestId: requestId,
        nextTimestamp,
      })) {
        return state
      }
      return {
        mergeRequests: {
          ...state.mergeRequests,
          [sessionId]: {
            phase: ok ? "succeeded" : "failed",
            requestId: requestId ?? previous.requestId,
            error: ok ? null : (error ?? null),
            mergeConflict: ok ? false : mergeConflict === true,
            failureKind: ok ? null : (failureKind ?? null),
            timestamp: nextTimestamp,
          },
        },
      }
    })
  },

  markMergeRequestIndeterminate: ({ sessionId, message, timestamp }) => {
    if (!sessionId) return
    set((state) => {
      const previous = state.mergeRequests[sessionId]
      if (!previous || previous.phase !== "requested") {
        return state
      }
      return {
        mergeRequests: {
          ...state.mergeRequests,
          [sessionId]: {
            phase: "indeterminate",
            requestId: previous.requestId,
            error: message,
            mergeConflict: false,
            failureKind: null,
            timestamp: resolveTimestamp(timestamp),
          },
        },
      }
    })
  },

  requestResolveConflict: ({ sessionId, requestId, conversationId, timestamp }) => {
    if (!sessionId || !requestId) return
    set((state) => ({
      resolveConflictActions: {
        ...state.resolveConflictActions,
        [sessionId]: {
          phase: "requested",
          requestId,
          conversationId: conversationId ?? null,
          baseBranch: null,
          dispatchMode: null,
          error: null,
          timestamp: resolveTimestamp(timestamp),
        },
      },
    }))
  },

  setResolveConflictFromServer: ({
    sessionId,
    requestId,
    conversationId,
    state: nextPhase,
    baseBranch,
    dispatchMode,
    error,
    timestamp,
  }) => {
    if (!sessionId) return
    set((state) => {
      const previous = state.resolveConflictActions[sessionId] ?? buildIdleResolveConflictActionState()
      const nextTimestamp = resolveTimestamp(timestamp)
      if (!shouldAcceptResolveConflictServerUpdate({
        previous,
        nextRequestId: requestId,
        nextTimestamp,
      })) {
        return state
      }
      const sameRequestId =
        typeof requestId === "string" &&
        requestId.length > 0 &&
        requestId === previous.requestId
      const effectiveNextPhase =
        sameRequestId && previous.phase === "dispatched" && nextPhase === "started"
          ? "dispatched"
          : nextPhase
      return {
        resolveConflictActions: {
          ...state.resolveConflictActions,
          [sessionId]: {
            phase: effectiveNextPhase,
            requestId: requestId ?? previous.requestId,
            conversationId: conversationId ?? previous.conversationId,
            baseBranch: baseBranch ?? previous.baseBranch,
            dispatchMode: dispatchMode ?? previous.dispatchMode ?? null,
            error: error ?? null,
            timestamp: nextTimestamp,
          },
        },
      }
    })
  },

  clearResolveConflictFromStatus: ({ sessionId, conversationId, timestamp, observedAt }) => {
    if (!sessionId) return
    set((state) => {
      const previous = state.resolveConflictActions[sessionId] ?? buildIdleResolveConflictActionState()
      const observedConversationId = typeof conversationId === "string" && conversationId.trim().length > 0
        ? conversationId.trim()
        : null
      if (observedConversationId) {
        const previousConversationId = previous.conversationId ?? "default"
        if (previousConversationId !== observedConversationId) {
          return state
        }
      }
      const nextTimestamp = resolveTimestamp(timestamp)
      const nextObservedAt = resolveTimestamp(observedAt ?? timestamp)
      const previousIsLocalHint = isLocalResolveConflictHintPhase(previous.phase)

      if (
        !previousIsLocalHint &&
        !isNewerOrEqualTimestamp({ previousTimestamp: previous.timestamp, nextTimestamp })
      ) {
        return state
      }

      if (
        previous.phase === "requested" &&
        shouldKeepResolveConflictRequestPending({
          requestedAt: previous.timestamp,
          observedMissingAt: nextObservedAt,
        })
      ) {
        return state
      }

      if (previous.phase === "requested") {
        return {
          resolveConflictActions: {
            ...state.resolveConflictActions,
            [sessionId]: {
              phase: "indeterminate",
              requestId: previous.requestId,
              conversationId: previous.conversationId,
              baseBranch: previous.baseBranch,
              dispatchMode: previous.dispatchMode,
              error: previous.error ?? RESOLVE_CONFLICT_STATUS_CONFIRMATION_MESSAGE,
              timestamp: nextObservedAt,
            },
          },
        }
      }

      if (previous.phase === "indeterminate") {
        if (shouldKeepResolveConflictIndeterminatePending({
          indeterminateAt: previous.timestamp,
          observedMissingAt: nextObservedAt,
        })) {
          return state
        }

        if (!isNewerOrEqualTimestamp({ previousTimestamp: previous.timestamp, nextTimestamp: nextObservedAt })) {
          return state
        }

        return {
          resolveConflictActions: {
            ...state.resolveConflictActions,
            [sessionId]: {
              phase: "idle",
              requestId: previous.requestId,
              conversationId: previous.conversationId,
              baseBranch: previous.baseBranch,
              dispatchMode: previous.dispatchMode,
              error: null,
              timestamp: nextObservedAt,
            },
          },
        }
      }

      if (previous.phase === "idle") {
        return state
      }

      return {
        resolveConflictActions: {
          ...state.resolveConflictActions,
          [sessionId]: {
            phase: "idle",
            requestId: null,
            conversationId: null,
            baseBranch: null,
            dispatchMode: null,
            error: null,
            timestamp: nextTimestamp,
          },
        },
      }
    })
  },

  markResolveConflictIndeterminate: ({ sessionId, message, timestamp }) => {
    if (!sessionId) return
    set((state) => {
      const previous = state.resolveConflictActions[sessionId]
      if (
        !previous ||
        previous.phase !== "requested"
      ) {
        return state
      }
      return {
        resolveConflictActions: {
          ...state.resolveConflictActions,
          [sessionId]: {
            phase: "indeterminate",
            requestId: previous.requestId,
            conversationId: previous.conversationId,
            baseBranch: previous.baseBranch,
            dispatchMode: previous.dispatchMode,
            error: message,
            timestamp: resolveTimestamp(timestamp),
          },
        },
      }
    })
  },

  clearMergeRequestState: (sessionId) => {
    if (!sessionId) return
    set((state) => {
      if (!Object.prototype.hasOwnProperty.call(state.mergeRequests, sessionId)) {
        return state
      }

      const nextMergeRequests = { ...state.mergeRequests }
      delete nextMergeRequests[sessionId]
      return {
        mergeRequests: nextMergeRequests,
      }
    })
  },

  clearSessionActionState: (sessionId) => {
    if (!sessionId) return
    set((state) => {
      const nextMergeRequests = { ...state.mergeRequests }
      const nextResolveConflictActions = { ...state.resolveConflictActions }
      delete nextMergeRequests[sessionId]
      delete nextResolveConflictActions[sessionId]
      return {
        mergeRequests: nextMergeRequests,
        resolveConflictActions: nextResolveConflictActions,
      }
    })
  },
}))
